/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import {
  $InstallerTokenFilters,
  GetInstallerTokensData,
  GetInstallerTokensResponse,
  InstallerToken,
  InstallerTokenFilters,
  InstallerTokenSortableFields,
} from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { usePaginatedQuery } from '@/hooks/query/usePaginatedQuery';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import { jsonSchemaToTypeMap } from '@/utils/filters';

export type UsePaginatedInstallerTokensQueryParams = {
  params?: Omit<GetInstallerTokensData, 'tenantId'>;
  type?: 'inMemory' | 'queryParams';
};

/**
 * Custom hook that fetches paginated installer tokens with filtering, sorting and real-time updates.
 * Combines React Query for data fetching with WebSocket subscriptions for real-time updates.
 *
 * @param options Configuration object:
 *   - params: Optional query parameters (filters, pagination, sort)
 *   - type: Storage type - 'inMemory' or 'queryParams' for URL persistence
 *
 * @returns Object containing:
 *   - data: Paginated installer tokens response
 *   - isLoading: Boolean for initial load state
 *   - isFetching: Boolean for any loading state
 *   - isError: Boolean for error state
 *   - error: Error object if request failed
 *   - updateQueryParams: Function to update filters, sort, and pagination
 *
 * Note: tenantId is automatically pulled from the management plane state store
 */
export function usePaginatedInstallerTokensQuery({ params, type }: UsePaginatedInstallerTokensQueryParams) {
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const selectedSubtenantId = useMgmtPlaneStateStore((state) => state.selectedSubtenantID);

  const filters = params?.filters || {};
  // Apply subtenant filter: use selected subtenant ID, or 'null' for tenant-level tokens
  if (!filters.subtenantId) {
    filters.subtenantId = selectedSubtenantId || 'null';
  }

  return usePaginatedQuery<
    GetInstallerTokensResponse,
    InstallerToken,
    InstallerTokenFilters,
    InstallerTokenSortableFields,
    GetInstallerTokensData
  >({
    apiCall: mgmtPlaneAPIClient.installerTokens.getInstallerTokens.bind(mgmtPlaneAPIClient.installerTokens),
    filterTypes: jsonSchemaToTypeMap($InstallerTokenFilters),
    queryKey: 'installerTokens',
    realtimeEvents: ['installer-token:created', 'installer-token:revoked'],
    params: {
      tenantId: selectedTenantId!,
      filters,
      ...params,
    },
    type,
    enabled: !!selectedTenantId,
  });
}
